import { renderQuality } from './renderQuality.js';

export class Projectile {
    constructor(x, y, vx, vy, damage, type = 'normal', range = null) {
        this.reset(x, y, vx, vy, damage, type, range);
    }

    reset(x, y, vx, vy, damage, type = 'normal', range = null) {
        this.x = x;
        this.y = y;
        this.startX = x;
        this.startY = y;
        this.vx = vx;
        this.vy = vy;
        this.radius = type === 'enemy' ? 4.5 : type === 'laser' ? 3 : 5;
        this.damage = damage;
        this.type = type;
        this.lifetime = 120;
        this.color = type === 'laser' ? '#ff3355'
                   : type === 'chain' ? '#ffe066'
                   : type === 'boomerang' ? '#ff8833'
                   : type === 'enemy' ? '#ff4488'
                   : type === 'bot' ? '#88ffcc'
                   : '#44e0ff';
        this.range = range; // max travel distance (world units); null = lifetime only
        this.returning = false;
        this.distanceTraveled = 0;
        if (!this.trail) this.trail = [];
        else this.trail.length = 0;
        this.spin = 0;
    }

    update(canvas, player = null) {
        const q = renderQuality();
        const maxTrail = q === 'high' ? 5 : q === 'medium' ? 2 : 0;
        if (maxTrail === 0) {
            this.trail.length = 0;
        } else {
            let slot = this.trail.length >= maxTrail ? this.trail.shift() : null;
            if (!slot) slot = { x: 0, y: 0 };
            slot.x = this.x;
            slot.y = this.y;
            this.trail.push(slot);
        }

        if (this.type === 'boomerang' && this.range && player) {
            const dx = this.x - this.startX;
            const dy = this.y - this.startY;
            this.distanceTraveled = Math.sqrt(dx * dx + dy * dy);

            if (this.distanceTraveled >= this.range && !this.returning) {
                this.returning = true;
            }

            if (this.returning && player) {
                const pdx = player.x - this.x;
                const pdy = player.y - this.y;
                const pdist = Math.sqrt(pdx * pdx + pdy * pdy);

                if (pdist > 5) {
                    const speed = 6;
                    this.vx = (pdx / pdist) * speed;
                    this.vy = (pdy / pdist) * speed;
                } else {
                    return false;
                }
            }
            this.spin += 0.35;
        }

        this.x += this.vx;
        this.y += this.vy;
        this.lifetime--;
        this.distanceTraveled = Math.hypot(this.x - this.startX, this.y - this.startY);

        // Hard max range so shots don't fly across the map unseen
        if (this.range != null && this.distanceTraveled >= this.range) {
            return false;
        }

        return this.lifetime > 0 &&
               this.x > -50 && this.x < canvas.width + 50 &&
               this.y > -50 && this.y < canvas.height + 50;
    }

    draw(ctx) {
        if (renderQuality() !== 'high') {
            ctx.shadowBlur = 0;
            ctx.fillStyle = this.color;
            if (this.trail.length) {
                ctx.globalAlpha = 0.35;
                const t = this.trail[0];
                ctx.beginPath();
                ctx.arc(t.x, t.y, this.radius * 0.65, 0, Math.PI * 2);
                ctx.fill();
            }
            ctx.globalAlpha = 1;
            ctx.beginPath();
            ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
            ctx.fill();
            return;
        }
        ctx.save();

        // Motion trail
        for (let i = 0; i < this.trail.length; i++) {
            const t = this.trail[i];
            const a = (i + 1) / (this.trail.length + 1) * 0.45;
            const r = this.radius * (0.35 + a * 0.65);
            ctx.globalAlpha = a;
            ctx.fillStyle = this.color;
            ctx.beginPath();
            ctx.arc(t.x, t.y, r, 0, Math.PI * 2);
            ctx.fill();
        }

        ctx.globalAlpha = 1;
        ctx.shadowBlur = 14;
        ctx.shadowColor = this.color;

        const speed = Math.hypot(this.vx, this.vy) || 1;
        const angle = Math.atan2(this.vy, this.vx);

        if (this.type === 'boomerang') {
            ctx.translate(this.x, this.y);
            ctx.rotate(this.spin);
            ctx.fillStyle = this.color;
            ctx.beginPath();
            ctx.arc(0, 0, this.radius + 2, 0.2, Math.PI - 0.2);
            ctx.arc(0, 0, this.radius - 1, Math.PI - 0.2, 0.2, true);
            ctx.closePath();
            ctx.fill();
            ctx.strokeStyle = '#fff8';
            ctx.lineWidth = 1.5;
            ctx.stroke();
        } else if (this.type === 'laser') {
            ctx.translate(this.x, this.y);
            ctx.rotate(angle);
            const len = 18;
            const grd = ctx.createLinearGradient(-len, 0, len, 0);
            grd.addColorStop(0, 'transparent');
            grd.addColorStop(0.5, this.color);
            grd.addColorStop(1, '#fff');
            ctx.fillStyle = grd;
            ctx.fillRect(-len, -2, len * 2, 4);
            ctx.fillStyle = '#fff';
            ctx.beginPath();
            ctx.arc(0, 0, 2.5, 0, Math.PI * 2);
            ctx.fill();
        } else {
            // Elongated energy bolt along velocity
            ctx.translate(this.x, this.y);
            ctx.rotate(angle);
            const len = Math.min(16, 6 + speed * 1.2);
            const grd = ctx.createRadialGradient(0, 0, 0, 0, 0, this.radius + 4);
            grd.addColorStop(0, '#ffffff');
            grd.addColorStop(0.35, this.color);
            grd.addColorStop(1, 'transparent');
            ctx.fillStyle = grd;
            ctx.beginPath();
            ctx.ellipse(0, 0, len * 0.55, this.radius, 0, 0, Math.PI * 2);
            ctx.fill();
            ctx.fillStyle = '#fff';
            ctx.globalAlpha = 0.9;
            ctx.beginPath();
            ctx.arc(0, 0, this.radius * 0.45, 0, Math.PI * 2);
            ctx.fill();
        }

        ctx.restore();
    }
}

/**
 * Particle kinds:
 *  - 'spark'  : fast streaks (default)
 *  - 'smoke'  : soft fading blobs
 *  - 'ring'   : expanding outline
 *  - 'glow'   : soft radial bloom
 *  - 'debris' : square shards
 */
export class Particle {
    constructor(x, y, color) {
        this.reset(x, y, color);
    }

    reset(x, y, color, kind = 'spark', opts = {}) {
        this.x = x;
        this.y = y;
        this.color = color || '#ffffff';
        this.kind = kind || 'spark';
        this.angle = opts.angle ?? Math.random() * Math.PI * 2;
        const speed = opts.speed ?? (this.kind === 'smoke' ? 1.2 : this.kind === 'ring' ? 0 : 2.5 + Math.random() * 3.5);
        this.vx = opts.vx ?? Math.cos(this.angle) * speed;
        this.vy = opts.vy ?? Math.sin(this.angle) * speed;
        this.radius = opts.radius ?? (this.kind === 'ring' ? 4 : this.kind === 'glow' ? 10 + Math.random() * 8 : Math.random() * 3 + 1.5);
        this.life = opts.life ?? (this.kind === 'smoke' ? 40 + Math.random() * 20 : this.kind === 'ring' ? 22 : 28 + Math.random() * 12);
        this.maxLife = this.life;
        this.growth = opts.growth ?? (this.kind === 'ring' ? 2.2 : this.kind === 'smoke' ? 0.25 : 0);
        this.friction = opts.friction ?? (this.kind === 'smoke' ? 0.92 : 0.94);
        this.rotation = Math.random() * Math.PI * 2;
        this.spin = (Math.random() - 0.5) * 0.3;
    }

    update() {
        this.x += this.vx;
        this.y += this.vy;
        this.vx *= this.friction;
        this.vy *= this.friction;
        this.radius += this.growth;
        this.rotation += this.spin;
        this.life--;
        return this.life > 0;
    }

    draw(ctx) {
        const t = this.life / this.maxLife;
        if (renderQuality() !== 'high') {
            const alpha = Math.max(0, t);
            ctx.globalAlpha = (this.kind === 'smoke' || this.kind === 'glow') ? alpha * 0.4 : alpha;
            ctx.shadowBlur = 0;
            if (this.kind === 'ring') {
                ctx.strokeStyle = this.color;
                ctx.lineWidth = 2;
                ctx.beginPath();
                ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
                ctx.stroke();
            } else if (this.kind === 'debris') {
                ctx.fillStyle = this.color;
                ctx.fillRect(this.x - this.radius, this.y - this.radius * 0.4, this.radius * 2, this.radius);
            } else {
                ctx.fillStyle = this.color;
                ctx.beginPath();
                ctx.arc(this.x, this.y, Math.max(0.8, this.radius), 0, Math.PI * 2);
                ctx.fill();
            }
            ctx.globalAlpha = 1;
            return;
        }
        ctx.save();
        ctx.globalAlpha = Math.max(0, t);

        if (this.kind === 'ring') {
            ctx.strokeStyle = this.color;
            ctx.lineWidth = 2.5 * t;
            ctx.shadowBlur = 12;
            ctx.shadowColor = this.color;
            ctx.beginPath();
            ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
            ctx.stroke();
        } else if (this.kind === 'glow') {
            const g = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, this.radius);
            g.addColorStop(0, this.color);
            g.addColorStop(1, 'transparent');
            ctx.globalAlpha = t * 0.55;
            ctx.fillStyle = g;
            ctx.beginPath();
            ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
            ctx.fill();
        } else if (this.kind === 'debris') {
            ctx.translate(this.x, this.y);
            ctx.rotate(this.rotation);
            ctx.fillStyle = this.color;
            ctx.shadowBlur = 8;
            ctx.shadowColor = this.color;
            const s = this.radius * (0.6 + t * 0.4);
            ctx.fillRect(-s, -s * 0.5, s * 2, s);
        } else if (this.kind === 'smoke') {
            const g = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, this.radius);
            g.addColorStop(0, this.color);
            g.addColorStop(1, 'transparent');
            ctx.globalAlpha = t * 0.35;
            ctx.fillStyle = g;
            ctx.beginPath();
            ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
            ctx.fill();
        } else {
            // spark — elongated streak
            const speed = Math.hypot(this.vx, this.vy);
            ctx.translate(this.x, this.y);
            ctx.rotate(Math.atan2(this.vy, this.vx));
            ctx.shadowBlur = 10;
            ctx.shadowColor = this.color;
            ctx.fillStyle = this.color;
            const len = this.radius * (1.5 + speed * 0.4);
            ctx.beginPath();
            ctx.ellipse(0, 0, len, this.radius * 0.55 * t + 0.5, 0, 0, Math.PI * 2);
            ctx.fill();
            ctx.fillStyle = '#fff';
            ctx.globalAlpha = t * 0.8;
            ctx.beginPath();
            ctx.arc(0, 0, this.radius * 0.35, 0, Math.PI * 2);
            ctx.fill();
        }

        ctx.restore();
    }
}
